import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import CenteredBox from '@/app/base-ui/CenterBox';
import { AddImage, ArrowRightBlack, CloseIcon } from '@/public/icon';
import Image from 'next/image';
import { Box, Button, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { MediaOperation } from '@/app/models/media/IMedia';
import ImagePicker from '../../MUI/ImagePicker';
import handleLogicText from '@/app/helpers/handleLogicContentPost';
import Filename from '../../MUI/FileDisplayHandler';
import { IGroup } from '@/app/models/group/IGroup';
import MUIBoxScrollBehavior from '../../MUI/BoxScrollBehavior';
import { fileHelper } from '@/app/helpers/handleFileBinary';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import { FileAttach } from '@/app/models/file/fileModel';
import SliderMedia from '@/app/base-ui/SwiperSliderMedia';
import ImageGrid from '../../Posts/Post/components/ImageGrid';
import { Media } from '@/app/models/media/MediaModel';
import { PostTypeConfig } from '@/app/constants/typedef';
import JoyPlayer from '@/app/base-ui/ReactPlayerVideo';
import ButtonHandleUrl from './ButtonHandleUrl';
import moment from 'moment';
import { useUser } from '@/app/hooks/useUser';
import utilsService from '@/app/helpers/utilsService';
import { CreatePostRequest, EditPostRequest } from '@/app/models/writing/WritingPosts';
import { cleanYouTubeUrl } from '../UrlFormWriting/components/YouTubeThumbnail';

interface WritingProps {
    state: CreatePostRequest | EditPostRequest;
    error?: any;
    group: IGroup | string;
    url: string | null;
    removeImage: (image: MediaOperation) => Promise<void>;
    handleAvatarChange: (selectedImages: MediaOperation[]) => Promise<void>;
    onChooseGroupAgain?: () => void;
    onBackToUrlScreen?: () => void;
    handleChangeCaption?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    status?: 'update' | 'create';
    optionWriting: 'image' | 'URL';
    showGroupArea?: boolean;
    fileAttachment?: FileAttach | null;
    onFile?: (dataFile: FileAttach | null) => void;
    onDeleteUrl?: (items: MediaOperation) => void;
}

const imageWidth = 80;
const imageHeight = 80;
const imageRadius = 10;

function isIGroup(group: IGroup | string): group is IGroup {
    return (group as IGroup).id !== undefined && (group as IGroup).name !== undefined;
}

const Writing: React.FC<WritingProps> = ({
    status = 'create',
    showGroupArea = false,
    state,
    group,
    url,
    fileAttachment,
    optionWriting = 'image',
    removeImage,
    handleAvatarChange,
    onChooseGroupAgain,
    handleChangeCaption,
    onDeleteUrl,
    onBackToUrlScreen,
    onFile,
}) => {
    const theme = useTheme();
    const [uploadedFile, setUploadedFile] = useState<FileAttach | null>(fileAttachment ?? null);
    const [ready, setReady] = useState<boolean>(false);
    const { user } = useUser();
    const { addSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isOwnerGroup = useMemo(() => isIGroup(group) && 'role' in group && group.role.id == 1, [group]);
    const canAttachFile = useMemo(() => user?.isExpert || user?.isInfluencer || isOwnerGroup, [user, isOwnerGroup]);
    const accessUpload = useMemo(() => !uploadedFile?.fileUrl, [uploadedFile?.fileUrl]);
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                addSnackbar({
                    message: '저희는 PDF 파일 형식만 지원합니다.',
                    severity: 'error',
                    autoHideDuration: 3000,
                    horizontal: 'center',
                    vertical: 'bottom',
                    type: 'toast',
                });
                event.target.value = '';
                return;
            }
            const { baseName, extension } = fileHelper.getFileNameAndExtension(file.name);
            const checkFileOver20MB = await fileHelper.checkFileSizeOver(file);
            if (checkFileOver20MB) {
                addSnackbar({
                    message: '최대 20Mb 까지만 업로드 가능합니다.',
                    severity: 'error',
                    autoHideDuration: 3000,
                    horizontal: 'center',
                    vertical: 'bottom',
                    type: 'toast',
                });
                setUploadedFile(null);
                return;
            }
            const fileSize = `${(file.size / (1024 * 1024)).toFixed(2)}MB`;
            const fileUrl = URL.createObjectURL(file);
            const fileLocal: FileAttach = {
                base: baseName,
                extension: extension,
                fileUrl,
                fileSize,
                file,
            };
            setUploadedFile(fileLocal);
            onFile?.(fileLocal);
            //Cao Thien -- 23/08/2024
            event.target.value = ''; // this code will be handle case upload two same file
            //Cao Thien -- 23/08/2024
        }
    };

    const deleteFile = (fileUrl: string) => {
        if (uploadedFile?.fileUrl == fileUrl) {
            URL.revokeObjectURL(uploadedFile.fileUrl);
        }
        setUploadedFile(null);
        onFile?.(null);
    };

    const handleDeleteUrl = (item: MediaOperation) => {
        onDeleteUrl?.(item);
    };

    const groupName = typeof group !== 'string' && 'name' in group ? group.name : 'all';
    const items = useMemo(
        () => [
            {
                type: 'group',
                title: '그룹 선택',
                icon: <ArrowRightBlack />,
                textSideIcon: group === 'all' ? '기본' : groupName,
                onClick: onChooseGroupAgain,
                isShow: showGroupArea,
                sx: { py: 5.25 },
            },
            {
                type: 'file',
                onClick: accessUpload ? () => fileInputRef.current?.click() : () => null,
                isShow: (status !== 'update' || (status == 'update' && 'file' in state && state.file)) && canAttachFile,
                title: '파일 업로드',
                icon: (
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: 'background.default',
                            color: 'grayscale.gray07',
                            maxHeight: '24px',
                            display: uploadedFile?.fileSize || status == 'update' ? 'none' : 'flex',
                            justifyContent: 'center',
                            maxWidth: '47px',
                            borderRadius: '15px',
                            textTransform: 'unset',
                            lineHeight: '100%',
                            alignItems: 'center',
                            padding: '6px 12px',
                            border: '1px solid var(--text-gray-07000, #000)',
                            '&:hover': {
                                backgroundColor: 'background.default',
                                opacity: 0.9,
                            },
                        }}
                    >
                        <Typography
                            variant="h2_12Medium"
                            sx={{ wordBreak: 'keep-all' }}
                            lineHeight="100%"
                        >
                            {'추가'}
                        </Typography>
                    </Box>
                ),
                textSideIcon: '',
                sx: { py: uploadedFile?.fileSize ? 5.25 : 4 },
            },
        ],
        [group, groupName, onChooseGroupAgain, showGroupArea, status, uploadedFile]
    );

    const filteredPhotos = state.media?.filter((item: any) => {
        if (!item) return false;
        if ('isDeleted' in item && item.isDeleted) return false;
        // When in image mode, filter out URL media to prevent rendering errors
        if (optionWriting === 'image' && 'mediaType' in item && item.mediaType === 'url') return false;
        return true;
    });
    const lengthSectionArea = useMemo(() => items.length > 0, [items]);

    const renderAreaMedia = () => {
        if (optionWriting == 'URL' && url) {
            return (
                <CenteredBox
                    sx={{
                        py: 0,
                        position: 'relative',
                        pb: 5,
                        minHeight: '210px',
                        flexDirection: 'column',
                        gap: 2.5,
                        height: '100%',
                        minWidth: '375px',
                        width: '100%',
                        background: theme.palette.background.default,
                    }}
                >
                    <JoyPlayer
                        uniqueId={`${url}${moment().millisecond()}`}
                        isResizeWithOutSwiper={true}
                        src={cleanYouTubeUrl(url)}
                        onReady={() => setReady(true)}
                        controls={false}
                    />
                    <ButtonHandleUrl
                        onEditUrl={onBackToUrlScreen}
                        onDeleteUrlCurrent={() => handleDeleteUrl(filteredPhotos[0])}
                    />
                </CenteredBox>
            );
        }
        if (status === 'update') {
            const shouldShow = state.media.length > 0;
            return (
                <CenteredBox sx={{ py: 0, position: 'relative', pb: 5, height: '100%', width: '100%', background: theme.palette.background.default, display: shouldShow ? 'flex' : 'none' }}>
                    <ImageGrid
                        showBullet={false}
                        data={state.media as Media[]}
                        type={PostTypeConfig.Posts}
                    />
                </CenteredBox>
            );
        }
        return (
            <MUIBoxScrollBehavior
                enableDragScroll={true}
                showScrollWhenHover={true}
                sx={{
                    '&::-webkit-scrollbar': { width: '.2rem', height: '.2rem' },
                    background: theme.palette.background.default,
                    px: { mobile: 5, tablet: 12.5 },
                    py: 5,
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    flexDirection: 'row',
                    overflow: 'auto',
                    width: '100%',
                }}
            >
                <Box sx={{ gap: 1, alignItems: 'center', display: 'flex', flexDirection: 'row' }}>
                    {filteredPhotos?.map((item: MediaOperation, index: number) => (
                        <Box
                            key={index}
                            sx={{
                                position: 'relative',
                                width: imageWidth,
                                height: imageHeight,
                                flexShrink: 0,
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    width: '20px',
                                    height: '20px',
                                    right: 4,
                                    zIndex: 1,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                }}
                                onClick={() => removeImage(item)}
                            >
                                <CloseIcon
                                    width="14"
                                    height="14"
                                />
                            </IconButton>
                            {'mediaType' in item && item?.mediaType === 'image' ? (
                                <Image
                                    draggable="false"
                                    src={'draftUrl' in item ? item.draftUrl : 'mediaUrl' in item ? item.mediaUrl ?? '' : ''}
                                    width={imageWidth}
                                    height={imageHeight}
                                    priority
                                    style={{ borderRadius: `${imageRadius}px`, objectFit: 'cover' }}
                                    alt="Image"
                                />
                            ) : (
                                <video
                                    width={imageWidth}
                                    height={imageHeight}
                                    style={{ borderRadius: `${imageRadius}px`, objectFit: 'cover' }}
                                    controls
                                >
                                    <source
                                        src={'draftUrl' in item ? item.draftUrl : 'mediaUrl' in item ? item.mediaUrl ?? '' : ''}
                                        type={'mediaUrl' in item ? fileHelper.getFileNameAndExtension(item.mediaUrl ?? '').extension : 'mp4'}
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </Box>
                    ))}
                    {filteredPhotos?.length < 10 && (
                        <Box
                            sx={{
                                width: imageWidth,
                                height: imageHeight,
                                flexShrink: 0,
                            }}
                        >
                            <ImagePicker
                                onChange={handleAvatarChange}
                                allowMultiple
                            >
                                <Button
                                    component="label"
                                    sx={{ width: '80px', padding: 0, height: '80px', bgcolor: '#fff', borderRadius: '10px' }}
                                >
                                    <AddImage />
                                </Button>
                            </ImagePicker>
                        </Box>
                    )}
                </Box>
            </MUIBoxScrollBehavior>
        );
    };

    const renderFileNameComponent = () => {
        if (uploadedFile) {
            return (
                <Filename
                    base={uploadedFile.base}
                    extension={uploadedFile.extension}
                    fileSize={uploadedFile.fileSize}
                    fileUrl={uploadedFile.fileUrl}
                    handleDeleteFile={deleteFile}
                    type={'id' in state && state.id ? 'edit' : 'create'}
                />
            );
        }
        if ('file' in state) {
            const { baseName, extension } = fileHelper.getFileNameAndExtension(state.file?.fileName ?? '');
            return (
                <Filename
                    base={baseName ?? ''}
                    extension={extension}
                    fileSize={`${utilsService.convertToMB(state?.file?.fileSize ?? 0)}MB`}
                    fileUrl={state.file?.mediaUrl ?? ''}
                    type={'edit'}
                />
            );
        }
        return null;
    };

    return (
        <Box sx={{ background: theme.palette.background.paper }}>
            {renderAreaMedia()}
            <Box
                sx={{
                    background: theme.palette.background.default,
                    pb: '58px',
                    display: 'flex',
                    alignItems:'center',
                    // paddingTop: state.media.length > 0 ? 0 : 5,
                    paddingTop: 0,
                    '& .MuiInputBase-root': {
                        fontSize: '12px',
                        padding: '0',
                    },
                    '& .MuiInputBase-input': {
                        padding: 0,
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: 'transparent',
                        },
                        '&:hover fieldset': {
                            borderColor: 'transparent',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#9e9e9e',
                    },
                }}
            >
                <TextField
                    sx={{
                        px: {
                            mobile: 5,
                            tablet: 12.5,
                            '& .MuiInputBase-root': {
                                fontSize: '12px',
                                fontWeight: 400,
                            },
                        },
                    }}
                    id="outlined-multiline-flexible"
                    onChange={handleChangeCaption}
                    value={state.caption ?? ''}
                    placeholder="내용을 입력하세요."
                    InputProps={{
                        sx: { fontSize: '12px' },
                    }}
                    fullWidth
                    multiline
                />
            </Box>
            <CenteredBox
                sx={{
                    width: '100%',
                    flexDirection: 'column',
                    px: lengthSectionArea ? { mobile: 5, tablet: 12.5 } : '',
                    borderTop: lengthSectionArea ? '' : `1px solid ${'#EEE'}`,
                    background: theme.palette.background.default,
                    mt: lengthSectionArea ? 2 : 0,
                }}
            >
                {items?.map(
                    (item, index) =>
                        item.isShow && (
                            <Box
                                key={item.title}
                                sx={{ borderBottom: `1px solid ${'#F9F9F9'}`, width: '100%' }}
                            >
                                <Button
                                    key={index}
                                    onClick={item.onClick}
                                    sx={{
                                        minHeight: '56px',
                                        borderRadius: '0',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        cursor: 'pointer',
                                        px: 0,
                                        ...item.sx,
                                    }}
                                >
                                    <Typography
                                        variant="h2_14Medium"
                                        color="grayscale.gray07"
                                        lineHeight="100%"
                                    >
                                        {item.title}
                                    </Typography>
                                    <CenteredBox sx={{ width: 'fit-content', gap: '8px' }}>
                                        <Typography
                                            color="primary.main"
                                            variant="h2_12Medium"
                                            lineHeight="100%"
                                            dangerouslySetInnerHTML={{
                                                __html: handleLogicText.shortText(item.textSideIcon, 10),
                                            }}
                                        />
                                        {item.icon && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>{item.icon}</Box>}
                                    </CenteredBox>
                                </Button>
                                {((uploadedFile && ['file'].includes(item?.type)) || (['file'].includes(item?.type) && status == 'update' && 'file' in state && state.file)) && canAttachFile && (
                                    <Box sx={{ background: theme.palette.background.default, width: '100%', pb: 5 }}>{renderFileNameComponent()}</Box>
                                )}
                            </Box>
                        )
                )}
            </CenteredBox>
            {status !== 'update' && canAttachFile && (
                <input
                    type="file"
                    accept="application/pdf"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
            )}
        </Box>
    );
};

export default Writing;
